import { Box, Button, Stack, Text } from '@chakra-ui/react'
import { faVideo } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useRouter } from 'next/router'
import { useCallback, useEffect, useState } from 'react'

import assertDefined from '@/utils/assertDefined'
import { pluralize } from '@/utils/helpers'
import playChimeSound from '@/utils/playChimeSound'

import * as url from '@/client/services/url'

import PostContents from '@/components/Community/Post/PostContents'
import ShwLink from '@/components/ShwLink'
import Collapsible from '@/components/UI/Collapsible'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import type { LiveHelpRequestWithSessionAndPost } from '@/components/Worlds/types'
import Avatar from '@/components/Worlds/UI/Avatar/Avatar'
import Card from '@/components/Worlds/UI/Card'

import CancelLiveHelpRequestModal from './CancelLiveHelpRequestModal'
import { LIVE_HELP_LEARNER_TIMEOUT_MINUTES } from './constants'

export default function JoinLiveHelpModal({
  open,
  onClose,
  liveHelpRequest,
}: {
  open: boolean
  onClose: () => void
  liveHelpRequest: LiveHelpRequestWithSessionAndPost
}): JSX.Element {
  const router = useRouter()
  const [showCancelRequestModal, setShowCancelRequestModal] = useState(false)

  const { session, post } = liveHelpRequest

  assertDefined(post, `Post not found for LiveHelpRequest#${liveHelpRequest}`)
  assertDefined(session, `Session not found for LiveHelpRequest#${liveHelpRequest}`)

  const { host, world } = session

  const maybeRedirectToPost = useCallback(async () => {
    const postUrl = url.getDashboardLiveHelpUrl({ postId: post.id })
    if (!url.helpers.routerMatchesUrl(router, postUrl)) {
      await router.push(postUrl)
    }
  }, [router, post.id])

  useEffect(() => {
    if (open) {
      playChimeSound()
    }
  }, [open])

  return (
    <>
      <CancelLiveHelpRequestModal
        postId={post.id}
        open={showCancelRequestModal}
        onClose={() => setShowCancelRequestModal(false)}
      />

      <ModalV3
        open={open}
        size="lg"
        onClose={onClose}
        disableClickOutside
        title="Join Live Help"
        body={() => (
          <Stack direction="column" width="100%" px="16px" spacing="16px">
            <Stack direction="row" justify="space-between" spacing="16px">
              <Box width="48px">
                <Avatar user={host.user} size="md" />
              </Box>
              <Text fontWeight="bold">
                {host.user.displayName} is ready to help you with your question in {world.title}!
              </Text>
            </Stack>

            <Collapsible
              title="Your Question"
              defaultIsOpen
              body={() => (
                <Card px="16px">
                  <PostContents
                    post={post}
                    onEdit={() => null}
                    onDelete={() => null}
                    onReport={() => null}
                    preview
                    truncateIfLong
                  />
                </Card>
              )}
            />

            <Text>
              Please join your tutor&apos;s room within{' '}
              {pluralize(LIVE_HELP_LEARNER_TIMEOUT_MINUTES, 'minute')} or they may help someone
              else.
            </Text>
          </Stack>
        )}
        leftFooterContent={() => (
          <Button variant="danger-outline" onClick={() => setShowCancelRequestModal(true)}>
            I don&apos;t need help anymore
          </Button>
        )}
        rightFooterContent={() => (
          <ShwLink href={url.getSessionJoinRedirectUrl(session.id, false)} isExternal>
            <Button leftIcon={<FontAwesomeIcon icon={faVideo} />} onClick={maybeRedirectToPost}>
              Join Zoom Room
            </Button>
          </ShwLink>
        )}
      />
    </>
  )
}
